// frontend/src/components/product/swatches/ProductSwatch.jsx

import React, { memo, useMemo } from "react";
import clsx from "clsx";

import "./ProductSwatch.css";

const FALLBACK_SWATCH = Object.freeze({
  kind: "colour",
  value: "#d7d0aa",
  label: "",
});

function buildConicGradient(values = []) {
  const colours = values.filter(Boolean).slice(0, 8);

  if (!colours.length) return "#d7d0aa";
  if (colours.length === 1) return colours[0];

  const step = 360 / colours.length;

  return `conic-gradient(${colours
    .map((colour, index) => {
      const start = Math.round(index * step);
      const end = Math.round((index + 1) * step);
      return `${colour} ${start}deg ${end}deg`;
    })
    .join(", ")})`;
}

const ProductSwatch = memo(function ProductSwatch({
  swatch = FALLBACK_SWATCH,
  className = "",
  label = "",
  title = "",
  role = "img",
}) {
  const safeSwatch = swatch || FALLBACK_SWATCH;
  const accessibleLabel = label || title || safeSwatch.label || "Colour swatch";

  const multiBackground = useMemo(() => {
    if (safeSwatch.kind !== "multi") return "";
    return buildConicGradient(
      Array.isArray(safeSwatch.values) ? safeSwatch.values : [],
    );
  }, [safeSwatch]);

  if (safeSwatch.kind === "image") {
    return (
      <span
        className={clsx(
          "product-swatch",
          "product-swatch--image",
          className,
          className ? `${className}--image` : "",
        )}
        style={{ backgroundImage: `url("${safeSwatch.value}")` }}
        title={title || safeSwatch.label || accessibleLabel}
        aria-label={accessibleLabel}
        role={role}
      />
    );
  }

  if (safeSwatch.kind === "multi") {
    return (
      <span
        className={clsx(
          "product-swatch",
          "product-swatch--multi",
          className,
          className ? `${className}--multi` : "",
        )}
        style={{
          "--product-swatch-multi": multiBackground,
          "--pdp-option-swatch": multiBackground,
          "--pdp-meta-swatch": multiBackground,
        }}
        title={title || safeSwatch.label || accessibleLabel}
        aria-label={accessibleLabel}
        role={role}
      />
    );
  }

  return (
    <span
      className={clsx("product-swatch", "product-swatch--colour", className)}
      style={{
        "--product-swatch-colour": safeSwatch.value || "#d7d0aa",
        "--pdp-option-swatch": safeSwatch.value || "#d7d0aa",
        "--pdp-meta-swatch": safeSwatch.value || "#d7d0aa",
      }}
      title={title || safeSwatch.label || accessibleLabel}
      aria-label={accessibleLabel}
      role={role}
    />
  );
});

export default ProductSwatch;